import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Briefcase } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const EmployeeTypes = () => {
  const [employeeTypes,         setEmployeeTypes]         = useState([]);
  const [filteredEmployeeTypes, setFilteredEmployeeTypes] = useState([]);
  const [loading,               setLoading]               = useState(true);
  const [searchTerm,            setSearchTerm]            = useState('');
  const [showModal,             setShowModal]             = useState(false);
  const [editMode,              setEditMode]              = useState(false);
  const [submitLoading,         setSubmitLoading]         = useState(false);
  const [errors,                setErrors]                = useState({});

  const [formData, setFormData] = useState({
    employeetypeid: null, typecode: '', typename: '', description: '', isactive: true,
  });

  useEffect(() => { fetchEmployeeTypes(); }, []);
  useEffect(() => { filterEmployeeTypes(); }, [searchTerm, employeeTypes]);

  const fetchEmployeeTypes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/EmployeeTypes.php');
      if (res.data.success) setEmployeeTypes(res.data.data || []);
      else alert('Failed to fetch employee types: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching employee types. Please check if backend is running.');
    } finally { setLoading(false); }
  };

  const filterEmployeeTypes = () => {
    if (!searchTerm.trim()) { setFilteredEmployeeTypes(employeeTypes); return; }
    const q = searchTerm.toLowerCase();
    setFilteredEmployeeTypes(employeeTypes.filter(t =>
      t.typename.toLowerCase().includes(q) ||
      t.typecode.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.typecode.trim()) errs.typecode = 'Type code is required';
    else if (formData.typecode.length > 20) errs.typecode = 'Type code must be 20 characters or less';
    else if (!/^[A-Z0-9_]+$/.test(formData.typecode)) errs.typecode = 'Uppercase letters, numbers, and underscores only';
    if (!formData.typename.trim()) errs.typename = 'Type name is required';
    else if (formData.typename.length > 50) errs.typename = 'Type name must be 50 characters or less';
    if (formData.description && formData.description.length > 500) errs.description = 'Description must be 500 characters or less';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/EmployeeTypes.php', { ...formData, isactive: formData.isactive ? 1 : 0 });
      if (res.data.success) {
        alert(editMode ? 'Employee type updated successfully!' : 'Employee type created successfully!');
        closeModal(); fetchEmployeeTypes();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error: ' + (err.response?.data?.message || 'Error saving employee type. Please try again.'));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (t) => {
    setFormData({ employeetypeid: t.employeetypeid, typecode: t.typecode, typename: t.typename, description: t.description || '', isactive: t.isactive === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id, typename) => {
    if (!window.confirm(`Are you sure you want to delete "${typename}"?`)) return;
    try {
      const res = await apiClient.delete('/EmployeeTypes.php', { data: { employeetypeid: id } });
      if (res.data.success) { alert('Employee type deleted successfully!'); fetchEmployeeTypes(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error: ' + (err.response?.data?.message || 'Error deleting employee type. Please try again.'));
    }
  };

  const resetForm    = () => { setFormData({ employeetypeid: null, typecode: '', typename: '', description: '', isactive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const handleTypeCodeChange = (e) => {
    setFormData({ ...formData, typecode: e.target.value.toUpperCase().replace(/\s/g, '_') });
  };

  const inputCls = (err) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Briefcase size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Employee Types Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage employee type classifications</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Employee Type
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by type code, name, or description..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredEmployeeTypes.length} {filteredEmployeeTypes.length === 1 ? 'type' : 'types'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading employee types...
          </div>
        ) : filteredEmployeeTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Briefcase size={48} className="text-gray-200 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No employee types found</p>
            {searchTerm ? (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            ) : (
              <button onClick={openAddModal}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Add your first employee type</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Type Code', 'Type Name', 'Description', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmployeeTypes.map(type => (
                  <tr key={type.employeetypeid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {type.typecode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{type.typename}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[300px] truncate">{type.description || '-'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        type.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {type.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(type)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(type.employeetypeid, type.typename)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[500px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Briefcase size={18} />
                {editMode ? 'Edit Employee Type' : 'Add New Employee Type'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Type Code */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Type Code <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.typecode} onChange={handleTypeCodeChange}
                  maxLength="20" placeholder="e.g., PERM, TEMP, CONTRACT"
                  disabled={editMode} autoFocus
                  className={inputCls(errors.typecode)} />
                {errors.typecode && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.typecode}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Uppercase letters, numbers, and underscores only</p>
              </div>

              {/* Type Name */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Type Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.typename}
                  onChange={e => setFormData({ ...formData, typename: e.target.value })}
                  maxLength="50" placeholder="e.g., Permanent, Temporary, Contract"
                  className={inputCls(errors.typename)} />
                {errors.typename && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.typename}</p>}
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Description</label>
                <textarea value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  maxLength="500" rows={3} placeholder="Optional description of this employee type"
                  className={`w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 resize-y font-[inherit] focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${errors.description ? 'border-red-500 bg-red-50' : 'border-gray-200'}`} />
                {errors.description && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.description}</p>}
                <p className="text-[11px] text-gray-400 mt-1 text-right">{formData.description.length}/500 characters</p>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive}
                    onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default EmployeeTypes;